import { useMutation, useQueryClient } from 'react-query';

import { PAYMENT_TYPE } from '@savgroup-front-common/types';
import { QuoteClientAnswer } from '@savgroup-front-common/types/Quotation/QuoteClientAnswer';

import { CommonRepairerService } from '../../../../api/Repairer';
import { useToasts } from '../../../../molecules/NotificationsProvider';
import { GetQuotationsByFileIdQuery } from '../../hooks/useGetQuotationsByFileIdQuery';
import messages from '../messages';

interface UseValidateQuotationArgs {
  quotationId: string;
  fileId: string;
  onAnswerSuccess?: () => void;
  paymentType: PAYMENT_TYPE;
}

interface UseValidateQuotationReturnValues {
  handleValidate: (paymentType?: PAYMENT_TYPE) => void;
  isValidateLoading: boolean;
}

export const useValidateQuotation = ({
  quotationId,
  fileId,
  onAnswerSuccess,
  paymentType,
}: UseValidateQuotationArgs): UseValidateQuotationReturnValues => {
  const queryClient = useQueryClient();
  const { pushErrors } = useToasts({
    extendedMessages: messages,
  });

  const { mutateAsync: handleValidate, isLoading: isValidateLoading } =
    useMutation([], async (overridePaymentType?: PAYMENT_TYPE) => {
      const response = await CommonRepairerService.setQuoteClientAnswerCommand({
        quoteId: quotationId,
        clientAnswer: QuoteClientAnswer.APPROVED,
        paymentType: overridePaymentType ?? paymentType,
      });

      if (response.failure) {
        pushErrors(response.errors);

        return response;
      }

      if (onAnswerSuccess) {
        onAnswerSuccess();
      }

      await queryClient.invalidateQueries({
        queryKey: [GetQuotationsByFileIdQuery, { fileId }],
      });
      await queryClient.invalidateQueries({
        queryKey: ['getInvoicesQuery', { fileId }],
      });

      return response;
    });

  return {
    handleValidate,
    isValidateLoading,
  };
};
